import { useMutation, useQueryClient, InvalidateQueryFilters } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openSnackbar } from '../AppSnackbar/snackbarSlice';
import {
  configUserMFA,
  validateUserMFACode,
  disableUserMFA,
  setPreferredUserMFA,
  MFAMethod,
} from './profilePageRequest';
import { Typography, Modal, Button, TextField, IconButton, InputAdornment, Card, Tooltip } from '@mui/material';
import { Visibility, VisibilityOff, ContentCopy, StarBorder } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import Spinner from 'react-bootstrap/Spinner';
import QRCode from 'react-qr-code';
import * as UserActions from '../Authorization/userSlice';
import * as yup from 'yup';

const STEPS = {
  PROMPT: 'PROMPT',
  DISABLE_SMS_MFA: 'DISABLE_SMS_MFA',
  DISABLE_TOTP_MFA: 'DISABLE_TOTP_MFA',
  CONFIG_SMS_MFA: 'CONFIG_SMS_MFA',
  CONFIG_TOTP_MFA: 'CONFIG_TOTP_MFA',
  ENTER_CODE: 'ENTER_CODE',
  LOADING: 'LOADING',
};

const MFA_METHODS = {
  SMS: 'SMS_MFA',
  TOTP: 'SOFTWARE_TOKEN_MFA',
} as const;

const SMS_MFA_PHONE_HELPER_TEXT = 'Include country code, e.g. +1 555-123-4567.';
const E164_PHONE_REGEX = /^\+[1-9]\d{7,14}$/;
const smsMfaPhoneSchema = yup
  .string()
  .transform((value) => (typeof value === 'string' ? value.trim() : value))
  .required('Phone number is required.')
  .test('has-country-code', 'Add country code with + (for example, +1).', (value) => {
    if (!value) return false;
    return value.startsWith('+');
  })
  .test('strict-e164-format', 'Phone must be strict international format like +12365136755 (no spaces).', (value) => {
    if (!value) return false;
    return E164_PHONE_REGEX.test(value);
  });

const useStyles = makeStyles((theme) => ({
  modalCard: {
    padding: '30px',
    width: 'min(560px, 92vw)',
    maxHeight: '90vh',
    overflowY: 'auto',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: '9999',
    justifyContent: 'center',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '18px',
    },
  },
  headerContainer: {
    paddingBottom: '.5vh',
    borderBottom: '1px gray solid',
  },
  descriptionContainer: {
    paddingTop: '10px',
  },
  fieldDiv: {
    paddingTop: '1vh',
    paddingBottom: '1vh',
    marginTop: '1vh',
    marginBottom: '1vh',
  },
  methodRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    backgroundColor: '#fafafa',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  methodInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  methodName: {
    margin: 0,
    lineHeight: 1.3,
  },
  methodActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
  },
  methodButtonsRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  preferredLinkButton: {
    minWidth: 0,
    padding: 0,
    height: 'auto',
    fontSize: '0.8rem',
    textTransform: 'none',
    justifyContent: 'flex-end',
  },
  statusBadgeEnabled: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '999px',
    backgroundColor: '#e8f5e9',
    color: '#1b5e20',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  statusBadgeDisabled: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '999px',
    backgroundColor: '#f5f5f5',
    color: '#616161',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  methodActionButton: {
    width: 'auto',
    minWidth: '88px',
    height: '40px',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: '1vw',
    marginTop: '3vh',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  button: {
    color: 'primary',
    width: '100%',
    height: '5vh',
    fontSize: '0.875rem',
  },
  closeButton: {
    backgroundColor: '#eeeeee',
    color: '#212121',
    width: '100%',
    height: '5vh',
    fontSize: '0.875rem',
  },
  continueButton: {
    backgroundColor: 'orange',
    color: 'black',
    width: '100%',
    height: '5vh',
    fontSize: '0.875rem',
  },
  resetButton: {
    backgroundColor: 'yellow',
    color: 'black',
    width: '100%',
    minHeight: '5vh',
    height: 'auto',
    fontSize: '0.7rem',
    whiteSpace: 'normal',
    lineHeight: 1.2,
    padding: '6px 10px',
  },
  spinnerContainer: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  },
  totpCodeContainer: {
    marginTop: '8px',
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: '8px 10px',
    width: 'fit-content',
    maxWidth: '100%',
    border: '1px solid #ddd',
    borderRadius: '8px',
    backgroundColor: '#fafafa',
    textAlign: 'left',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  totpActions: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '8px',
  },
  totpQrWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
}));

interface MFAModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
}

interface FormData {
  userId: string;
  phone: string;
  password: string;
}

export default function MFAModal({ isModalOpen, setIsModalOpen }: MFAModalProps) {
  const classes = useStyles();
  const [session, setSession] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [selectedMethod, setSelectedMethod] = useState<MFAMethod>(MFA_METHODS.SMS);
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [showManualCode, setShowManualCode] = useState(false);
  const [currentStep, setCurrentStep] = useState(STEPS.PROMPT);
  const currentUserInfo = useSelector((state: any) => state.user.userInfo);
  const [formData, setFormData] = useState<FormData>({
    userId: currentUserInfo.id,
    phone: '',
    password: '',
  });
  const [phoneValidationError, setPhoneValidationError] = useState<string | null>(null);

  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const queryClient = useQueryClient();

  const normalizedMfaSettings: string[] = Array.isArray(currentUserInfo?.mfaSettings)
    ? currentUserInfo.mfaSettings
    : typeof currentUserInfo?.mfaSettings === 'string'
    ? [currentUserInfo.mfaSettings]
    : [];

  const isMfaMethodEnabled = (method: MFAMethod) =>
    currentUserInfo?.mfaEnabled === 'Enabled' && normalizedMfaSettings.includes(method);

  const normalizedPreferredMethod: MFAMethod | '' =
    currentUserInfo?.preferredMfaMethod === MFA_METHODS.SMS || currentUserInfo?.preferredMfaMethod === MFA_METHODS.TOTP
      ? currentUserInfo.preferredMfaMethod
      : '';
  const enabledMethodCount = [MFA_METHODS.SMS, MFA_METHODS.TOTP].filter((method) => isMfaMethodEnabled(method)).length;

  const buildTotpOtpAuthUri = (): string | null => {
    if (!totpSecret) return null;
    const issuer = encodeURIComponent('Broadsight Tracker');
    const accountName = encodeURIComponent(currentUserInfo?.email || currentUserInfo?.username || formData.userId);
    return `otpauth://totp/${issuer}:${accountName}?secret=${encodeURIComponent(totpSecret)}&issuer=${issuer}`;
  };

  const handleError = (err: any, errorMessage: string) => {
    dispatch(
      openSnackbar({
        message: errorMessage,
        severity: 'error',
      })
    );
    console.error(err);
    setIsModalOpen(false);
  };

  const handleConfigMFA = async (form: FormData, method: MFAMethod) => {
    setCurrentStep(STEPS.LOADING);
    const data = await configUserMFA(form, method);
    return data;
  };

  const configMfaMutation = useMutation({
    mutationFn: () => handleConfigMFA(formData, selectedMethod),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({
        queryKey: ['configMfa', 'ProfilePage'] as InvalidateQueryFilters['queryKey'],
      });

      setSession(data.session);
      setTotpSecret(data.secretCode ?? data.totpSecret ?? data.secret ?? null);
      setShowManualCode(false);
      setCurrentStep(STEPS.ENTER_CODE);
    },
    onError: (err: any) => handleError(err, 'Unable to complete MFA setup'),
  });

  const validateMfaMutation = useMutation({
    mutationFn: () => {
      if (selectedMethod === MFA_METHODS.SMS && !session) {
        throw new Error('session is not valid');
      }

      return validateUserMFACode(formData.userId, session, code, selectedMethod);
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({
        queryKey: ['validateMfa', 'ProfilePage'] as InvalidateQueryFilters['queryKey'],
      });

      if (data?.id_token && data?.access_token) {
        dispatch(UserActions.setIdToken(data.id_token));
        dispatch(UserActions.setAccessToken(data.access_token));
      }

      const updatedUserInfo = {
        ...currentUserInfo,
        phone: selectedMethod === MFA_METHODS.SMS ? formData.phone : currentUserInfo.phone,
        mfaSettings: data.mfaSettings,
        mfaEnabled: data.mfaEnabled,
        preferredMfaMethod: data.preferredMfaMethod,
      };

      dispatch(UserActions.setUserInfo(updatedUserInfo));

      dispatch(
        openSnackbar({
          message: 'MFA code validated',
          severity: 'success',
        })
      );
      setIsModalOpen(false);
    },
    onError: (err: any) => handleError(err, 'Unable to validate MFA code'),
  });

  const disableMfaMutation = useMutation({
    mutationFn: () => disableUserMFA(formData, selectedMethod),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({
        queryKey: ['disableMfa', 'ProfilePage'] as InvalidateQueryFilters['queryKey'],
      });

      const updatedUserInfo = {
        ...currentUserInfo,
        phone: selectedMethod === MFA_METHODS.SMS ? 'Not Configured' : currentUserInfo.phone,
        mfaSettings: data?.mfaSettings ?? normalizedMfaSettings.filter((method: string) => method !== selectedMethod),
        mfaEnabled: data?.mfaEnabled ?? 'Disabled',
        preferredMfaMethod: data?.preferredMfaMethod ?? 'Not Configured',
      };

      dispatch(UserActions.setUserInfo(updatedUserInfo));

      dispatch(
        openSnackbar({
          message: selectedMethod === MFA_METHODS.SMS ? 'SMS MFA disabled' : 'TOTP MFA disabled',
          severity: 'warning',
        })
      );
      setIsModalOpen(false);
    },
    onError: (err: any) =>
      handleError(err, selectedMethod === MFA_METHODS.SMS ? 'Unable to disable SMS MFA' : 'Unable to disable TOTP MFA'),
  });

  const setPreferredMfaMutation = useMutation({
    mutationFn: (method: MFAMethod) => setPreferredUserMFA(formData.userId, method),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({
        queryKey: ['mfa', 'profilePage'] as InvalidateQueryFilters['queryKey'],
      });

      const updatedUserInfo = {
        ...currentUserInfo,
        mfaSettings: data.mfaSettings,
        mfaEnabled: data.mfaEnabled,
        preferredMfaMethod: data.preferredMfaMethod,
      };

      dispatch(UserActions.setUserInfo(updatedUserInfo));
      dispatch(
        openSnackbar({
          message: 'Preferred MFA method updated',
          severity: 'success',
        })
      );
    },
    onError: (err: any) => {
      dispatch(
        openSnackbar({
          message: 'Unable to update preferred MFA method',
          severity: 'error',
        })
      );
      console.error(err);
    },
  });

  const handleResetSmsMfa = () => {
    setSelectedMethod(MFA_METHODS.SMS);
    setSession(null);
    setCode('');
    setTotpSecret(null);
    setShowManualCode(false);
    setPhoneValidationError(null);
    setFormData({
      userId: currentUserInfo.id,
      phone: '',
      password: '',
    });
    setCurrentStep(STEPS.CONFIG_SMS_MFA);
  };

  const handleResetTotpMfa = () => {
    setSelectedMethod(MFA_METHODS.TOTP);
    setSession(null);
    setCode('');
    setTotpSecret(null);
    setShowManualCode(false);
    setPhoneValidationError(null);
    setFormData({
      userId: currentUserInfo.id,
      phone: '',
      password: '',
    });
    setCurrentStep(STEPS.CONFIG_TOTP_MFA);
  };

  useEffect(() => {
    if (!isModalOpen) {
      setSession(null);
      setCode('');
      setSelectedMethod(MFA_METHODS.SMS);
      setTotpSecret(null);
      setShowManualCode(false);
      setPhoneValidationError(null);
      setFormData({
        userId: currentUserInfo.id,
        phone: '',
        password: '',
      });
      setCurrentStep(STEPS.PROMPT);
    }
  }, [isModalOpen, currentUserInfo.id, normalizedPreferredMethod]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleFormChange = (field: { name: string; value: string }) => {
    const { name, value } = field;
    setFormData({ ...formData, [name]: value });
  };

  const getPhoneValidationError = (phoneInput: string): string | null => {
    try {
      smsMfaPhoneSchema.validateSync(phoneInput);
      return null;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        return err.message;
      }
      return 'Invalid phone number.';
    }
  };

  const handlePhoneNumberChange = (value: string) => {
    handleFormChange({ name: 'phone', value });
    if (phoneValidationError) {
      setPhoneValidationError(getPhoneValidationError(value));
    }
  };

  const handleSetupMFA = (method: MFAMethod) => {
    setSelectedMethod(method);
    setCurrentStep(method === MFA_METHODS.SMS ? STEPS.CONFIG_SMS_MFA : STEPS.CONFIG_TOTP_MFA);
  };

  const handleDisableMFA = (method: MFAMethod) => {
    setSelectedMethod(method);
    setCurrentStep(method === MFA_METHODS.SMS ? STEPS.DISABLE_SMS_MFA : STEPS.DISABLE_TOTP_MFA);
  };

  const handleSetPreferredMfa = (method: MFAMethod) => {
    if (method === normalizedPreferredMethod) return;
    setPreferredMfaMutation.mutate(method);
  };

  const handleConfigSMSMFASubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const phoneError = getPhoneValidationError(formData.phone);

    if (phoneError) {
      setPhoneValidationError(phoneError);
      dispatch(
        openSnackbar({
          message: `${phoneError} ${SMS_MFA_PHONE_HELPER_TEXT}`,
          severity: 'warning',
        })
      );
      return;
    }

    setSelectedMethod(MFA_METHODS.SMS);
    setTotpSecret(null);
    setShowManualCode(false);
    setPhoneValidationError(null);
    configMfaMutation.mutate();
  };

  const handleConfigTOTPMFASubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    setSelectedMethod(MFA_METHODS.TOTP);
    setShowManualCode(false);
    configMfaMutation.mutate();
  };

  const formatTotpSecretRows = (secret: string): string[] => {
    // Group key into readable 4-char chunks and 4 chunks per line.
    const groups = secret.match(/.{1,4}/g) ?? [secret];
    const rows: string[] = [];
    for (let i = 0; i < groups.length; i += 4) {
      rows.push(groups.slice(i, i + 4).join(' '));
    }
    return rows;
  };

  const handleCopyTotpSecret = async () => {
    if (!totpSecret) return;
    try {
      await navigator.clipboard.writeText(totpSecret);
      dispatch(
        openSnackbar({
          message: 'Setup key copied',
          severity: 'success',
        })
      );
    } catch (err) {
      dispatch(
        openSnackbar({
          message: 'Unable to copy setup key',
          severity: 'error',
        })
      );
    }
  };

  const prevStep = () => {
    switch (currentStep) {
      case STEPS.CONFIG_SMS_MFA:
        setCurrentStep(STEPS.PROMPT);
        break;
      case STEPS.CONFIG_TOTP_MFA:
        setCurrentStep(STEPS.PROMPT);
        break;
      case STEPS.ENTER_CODE:
        setCurrentStep(selectedMethod === MFA_METHODS.SMS ? STEPS.CONFIG_SMS_MFA : STEPS.CONFIG_TOTP_MFA);
        break;
      case STEPS.DISABLE_SMS_MFA:
        setCurrentStep(STEPS.PROMPT);
        break;
      case STEPS.DISABLE_TOTP_MFA:
        setCurrentStep(STEPS.PROMPT);
        break;
      default:
        break;
    }
  };

  const renderPasswordPrompt = () => {
    return (
      <div className={classes.fieldDiv}>
        <TextField
          label="Password"
          variant="standard"
          fullWidth
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          autoComplete="current-password"
          onChange={(e) => {
            handleFormChange({ name: 'password', value: e.target.value });
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case STEPS.PROMPT:
        return (
          <div>
            <div className={classes.headerContainer}>
              <Typography align="left" variant="h5">
                MFA Settings
              </Typography>
            </div>
            <div className={classes.descriptionContainer}>
              <Typography align="left" variant="body1">
                Manage each MFA method below. Set up, update, disable, and choose your preferred method directly from
                this screen.
              </Typography>
            </div>
            <div className={`${classes.fieldDiv} ${classes.methodRow}`}>
              <div className={classes.methodInfo}>
                <Typography align="left" variant="subtitle1" className={classes.methodName}>
                  Text message (SMS)
                </Typography>
                <span
                  className={
                    isMfaMethodEnabled(MFA_METHODS.SMS) ? classes.statusBadgeEnabled : classes.statusBadgeDisabled
                  }
                  data-cy="status-smsMfa-mfaModal"
                >
                  {isMfaMethodEnabled(MFA_METHODS.SMS) ? 'Enabled' : 'Not Configured'}
                </span>
              </div>
              <div className={classes.methodActions}>
                {!isMfaMethodEnabled(MFA_METHODS.SMS) ? (
                  <Button
                    className={classes.methodActionButton}
                    variant="contained"
                    onClick={() => handleSetupMFA(MFA_METHODS.SMS)}
                    data-cy="button-setupSmsMfa-mfaModal"
                  >
                    Set Up
                  </Button>
                ) : (
                  <>
                    <div className={classes.methodButtonsRow}>
                      <Button
                        className={classes.methodActionButton}
                        variant="outlined"
                        onClick={handleResetSmsMfa}
                        data-cy="button-editSmsMfa-mfaModal"
                      >
                        Edit
                      </Button>
                      <Button
                        className={classes.methodActionButton}
                        variant="contained"
                        onClick={() => handleDisableMFA(MFA_METHODS.SMS)}
                        data-cy="button-disableSmsMfa-mfaModal"
                      >
                        Disable
                      </Button>
                    </div>
                    {enabledMethodCount > 1 && normalizedPreferredMethod !== MFA_METHODS.SMS && (
                      <Button
                        className={classes.preferredLinkButton}
                        variant="text"
                        size="small"
                        startIcon={<StarBorder fontSize="small" />}
                        onClick={() => handleSetPreferredMfa(MFA_METHODS.SMS)}
                        data-cy="button-preferSmsMfa-mfaModal"
                      >
                        Set as Preferred
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className={`${classes.fieldDiv} ${classes.methodRow}`}>
              <div className={classes.methodInfo}>
                <Typography align="left" variant="subtitle1" className={classes.methodName}>
                  Authenticator app
                </Typography>
                <span
                  className={
                    isMfaMethodEnabled(MFA_METHODS.TOTP) ? classes.statusBadgeEnabled : classes.statusBadgeDisabled
                  }
                  data-cy="status-totpMfa-mfaModal"
                >
                  {isMfaMethodEnabled(MFA_METHODS.TOTP) ? 'Enabled' : 'Not Configured'}
                </span>
              </div>
              <div className={classes.methodActions}>
                {!isMfaMethodEnabled(MFA_METHODS.TOTP) ? (
                  <Button
                    className={classes.methodActionButton}
                    variant="contained"
                    onClick={() => handleSetupMFA(MFA_METHODS.TOTP)}
                    data-cy="button-setupTotpMfa-mfaModal"
                  >
                    Set Up
                  </Button>
                ) : (
                  <>
                    <div className={classes.methodButtonsRow}>
                      <Button
                        className={classes.methodActionButton}
                        variant="outlined"
                        onClick={handleResetTotpMfa}
                        data-cy="button-editTotpMfa-mfaModal"
                      >
                        Edit
                      </Button>
                      <Button
                        className={classes.methodActionButton}
                        variant="contained"
                        onClick={() => handleDisableMFA(MFA_METHODS.TOTP)}
                        data-cy="button-disableTotpMfa-mfaModal"
                      >
                        Disable
                      </Button>
                    </div>
                    {enabledMethodCount > 1 && normalizedPreferredMethod !== MFA_METHODS.TOTP && (
                      <Button
                        className={classes.preferredLinkButton}
                        variant="text"
                        size="small"
                        startIcon={<StarBorder fontSize="small" />}
                        onClick={() => handleSetPreferredMfa(MFA_METHODS.TOTP)}
                        data-cy="button-preferTotpMfa-mfaModal"
                      >
                        Set as Preferred
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
            {enabledMethodCount > 1 && normalizedPreferredMethod && (
              <div className={classes.fieldDiv}>
                <Typography align="left" variant="body2">
                  Preferred method:{' '}
                  {normalizedPreferredMethod === MFA_METHODS.SMS ? 'Text message (SMS)' : 'Authenticator app'}
                </Typography>
              </div>
            )}
          </div>
        );
      case STEPS.DISABLE_SMS_MFA:
        return (
          <div>
            <div className={classes.headerContainer}>
              <Typography align="left" variant="h5">
                Disable SMS MFA
              </Typography>
            </div>
            <div className={classes.descriptionContainer}>
              <Typography align="left" variant="body1">
                Are you sure you want to turn off SMS MFA for your account?
              </Typography>
            </div>
          </div>
        );
      case STEPS.CONFIG_SMS_MFA:
        return (
          <div>
            <Typography align="left" variant="body1">
              Enter the phone number that you'd like to use to receive MFA codes.
            </Typography>
            <form id="sms-mfa-config-form" onSubmit={handleConfigSMSMFASubmit}>
              <div className={classes.fieldDiv}>
                <TextField
                  label="Phone Number"
                  variant="standard"
                  fullWidth
                  value={formData.phone}
                  autoComplete="tel"
                  type="tel"
                  onChange={(e) => handlePhoneNumberChange(e.target.value)}
                  onBlur={() => setPhoneValidationError(getPhoneValidationError(formData.phone))}
                  error={Boolean(phoneValidationError)}
                  helperText={phoneValidationError || SMS_MFA_PHONE_HELPER_TEXT}
                />
                {renderPasswordPrompt()}
              </div>
            </form>
          </div>
        );
      case STEPS.CONFIG_TOTP_MFA:
        return (
          <div>
            <Typography align="left" variant="body1">
              Enter your password to start TOTP setup. You will then receive a secret to add in your authenticator app.
            </Typography>
            <form id="totp-mfa-config-form" onSubmit={handleConfigTOTPMFASubmit}>
              <div className={classes.fieldDiv}>{renderPasswordPrompt()}</div>
            </form>
          </div>
        );
      case STEPS.ENTER_CODE:
        return (
          <div>
            <Typography align="left" variant="body1">
              {selectedMethod === MFA_METHODS.SMS
                ? 'Enter the 6 digit code received on the phone number you entered earlier. Click re-send if the timer runs out to get another code.'
                : 'Enter the 6 digit code generated by your authenticator app.'}
            </Typography>
            {selectedMethod === MFA_METHODS.TOTP && (
              <div className={classes.fieldDiv}>
                <div className={classes.totpQrWrapper}>
                  {buildTotpOtpAuthUri() ? (
                    <QRCode value={buildTotpOtpAuthUri() || ''} size={220} />
                  ) : (
                    <Typography align="left" variant="body2">
                      QR setup is not available right now.
                    </Typography>
                  )}
                  {!showManualCode && totpSecret && (
                    <div className={classes.totpActions}>
                      <Button
                        variant="text"
                        onClick={() => setShowManualCode(true)}
                        data-cy="button-showTotpManualCode-mfaModal"
                      >
                        Show setup key instead
                      </Button>
                    </div>
                  )}
                </div>
                {showManualCode && totpSecret && (
                  <>
                    <div className={classes.totpCodeContainer}>
                      <Typography align="left" variant="body2">
                        Authenticator setup key:
                      </Typography>
                      {formatTotpSecretRows(totpSecret).map((row) => (
                        <Typography key={row} align="left" variant="body2">
                          <strong>{row}</strong>
                        </Typography>
                      ))}
                    </div>
                    <div className={classes.totpActions}>
                      <Tooltip title="Copy setup key">
                        <IconButton
                          onClick={handleCopyTotpSecret}
                          size="small"
                          data-cy="button-copyTotpManualCode-mfaModal"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </>
                )}
              </div>
            )}
            <div className={classes.fieldDiv}>
              <TextField label="MFA Code" variant="standard" value={code} onChange={(e) => setCode(e.target.value)} />
            </div>
            {selectedMethod === MFA_METHODS.SMS
              ? 'Your code will expire in 24 hours.'
              : 'Codes refresh every 30 seconds.'}
          </div>
        );
      case STEPS.DISABLE_TOTP_MFA:
        return (
          <div>
            <div className={classes.headerContainer}>
              <Typography align="left" variant="h5">
                Disable Authenticator-App MFA
              </Typography>
            </div>
            <div className={classes.descriptionContainer}>
              <Typography align="left" variant="body1">
                Are you sure you want to turn off authenticator-app MFA for your account?
              </Typography>
            </div>
          </div>
        );
      default:
        return (
          <div>
            <Spinner animation="border" />
          </div>
        );
    }
  };

  const hasBackButton = currentStep !== STEPS.PROMPT && currentStep !== STEPS.LOADING;

  return (
    <Modal
      open={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      aria-labelledby="mfa-modal-title"
      aria-describedby="mfa-modal-description"
    >
      <Card className={classes.modalCard}>
        <div>{renderStepContent()}</div>
        <div className={classes.buttonContainer}>
          {hasBackButton && (
            <Button className={classes.button} variant="contained" onClick={prevStep}>
              Back
            </Button>
          )}
          <Button variant="contained" className={classes.closeButton} onClick={() => setIsModalOpen(false)}>
            Close
          </Button>
          {currentStep === STEPS.ENTER_CODE && (
            <Button className={classes.button} variant="contained" onClick={() => validateMfaMutation.mutate()}>
              Verify Code
            </Button>
          )}
          {currentStep === STEPS.CONFIG_SMS_MFA && (
            <Button className={classes.button} variant="contained" type="submit" form="sms-mfa-config-form">
              Request Code
            </Button>
          )}
          {currentStep === STEPS.CONFIG_TOTP_MFA && (
            <Button className={classes.button} variant="contained" type="submit" form="totp-mfa-config-form">
              Generate TOTP Secret
            </Button>
          )}
          {currentStep === STEPS.DISABLE_SMS_MFA && (
            <Button variant="contained" className={classes.continueButton} onClick={() => disableMfaMutation.mutate()}>
              Disable
            </Button>
          )}
          {currentStep === STEPS.DISABLE_TOTP_MFA && (
            <Button variant="contained" className={classes.continueButton} onClick={() => disableMfaMutation.mutate()}>
              Disable
            </Button>
          )}
        </div>
      </Card>
    </Modal>
  );
}
